import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import styles from "./MosLinks.module.css";
import { generateMosLinkPdf } from "../../utils/generateMosLinkPdf";

const API = process.env.REACT_APP_API_URL;

function authHeaders() {
  try {
    const stored = JSON.parse(localStorage.getItem("AUTH_DATA") || "{}");
    return {
      "Content-Type": "application/json",
      Authorization: `Bearer ${stored.jwt}`,
    };
  } catch {
    return { "Content-Type": "application/json" };
  }
}

const BASE_URL = (process.env.REACT_APP_FE_URL || window.location.origin).replace(/\/$/, "");

function buildUrl(token) {
  return `${BASE_URL}/mos/${token}`;
}

const EyeOn = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
    <circle cx="12" cy="12" r="3"/>
  </svg>
);

const EyeOff = () => (
  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
    <line x1="1" y1="1" x2="23" y2="23"/>
  </svg>
);

function SortTh({ col, label, sortBy, sortDir, onSort, className = "" }) {
  const active = sortBy === col;
  return (
    <th className={`${styles.sortTh} ${className}`} onClick={() => onSort(col)}>
      {label}
      <span className={styles.sortIcon}>
        {active ? (sortDir === "asc" ? " ▲" : " ▼") : " ⇅"}
      </span>
    </th>
  );
}

export default function MosLinks() {
  const navigate = useNavigate();
  const [links, setLinks]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [creating, setCreating]   = useState(false);
  const [copied, setCopied]       = useState(null);
  const [visibleCodes, setVisible] = useState(new Set());
  const [generatingPdf, setGeneratingPdf] = useState(null);

  // Modal state
  const [showModal, setShowModal]   = useState(false);
  const [modalName, setModalName]   = useState("");
  const [modalVorname, setModalVorname] = useState("");

  const toggleCode = (id) => {
    setVisible((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };
  const [page, setPage]         = useState(1);
  const [perPage, setPerPage]   = useState(10);
  const [meta, setMeta]         = useState({ current_page: 1, last_page: 1, total: 0 });
  const [sortBy, setSortBy]     = useState("created_at");
  const [sortDir, setSortDir]   = useState("desc");
  const [search, setSearch]     = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");

  useEffect(() => {
    const t = setTimeout(() => { setDebouncedSearch(search); setPage(1); }, 300);
    return () => clearTimeout(t);
  }, [search]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({
        page,
        per_page: perPage,
        sort_by:  sortBy,
        sort_dir: sortDir,
        search:   debouncedSearch,
      });
      const res  = await fetch(`${API}/api/mos-links?${params}`, { headers: authHeaders() });
      const data = await res.json();
      setLinks(data.data ?? []);
      setMeta({
        current_page: data.current_page ?? 1,
        last_page:    data.last_page    ?? 1,
        total:        data.total        ?? 0,
      });
    } finally {
      setLoading(false);
    }
  }, [page, perPage, sortBy, sortDir, debouncedSearch]);

  useEffect(() => { load(); }, [load]);

  const handleSort = (col) => {
    if (col === sortBy) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortBy(col);
      setSortDir("desc");
    }
    setPage(1);
  };

  const handlePerPageChange = (e) => {
    setPerPage(Number(e.target.value));
    setPage(1);
  };

  const openModal = () => { setModalName(""); setModalVorname(""); setShowModal(true); };
  const closeModal = () => { setShowModal(false); };

  // Optimistic: prepend new row, no full reload
  const handleCreate = async () => {
    setCreating(true);
    setShowModal(false);
    try {
      const res  = await fetch(`${API}/api/mos-links`, {
        method: "POST",
        headers: authHeaders(),
        body: JSON.stringify({
          name:    modalName.trim()    || null,
          vorname: modalVorname.trim() || null,
        }),
      });
      const link = await res.json();
      setLinks((prev) => [link, ...prev].slice(0, perPage));
      setMeta((m) => ({
        ...m,
        total:     m.total + 1,
        last_page: Math.ceil((m.total + 1) / perPage),
      }));
    } finally {
      setCreating(false);
    }
  };

  // Optimistic: remove row, no full reload
  const handleDelete = async (id) => {
    setLinks((prev) => prev.filter((l) => l.id !== id));
    setMeta((m) => ({
      ...m,
      total:     Math.max(0, m.total - 1),
      last_page: Math.max(1, Math.ceil((m.total - 1) / perPage)),
    }));
    await fetch(`${API}/api/mos-links/${id}`, { method: "DELETE", headers: authHeaders() });
  };

  const handleUnlock = async (id) => {
    const res = await fetch(`${API}/api/mos-links/${id}/unlock`, { method: "POST", headers: authHeaders() });
    if (res.ok) {
      const updated = await res.json();
      setLinks((prev) => prev.map((l) => (l.id === id ? { ...l, ...updated } : l)));
    }
  };

  const handlePdf = async (id, token, code, name, vorname) => {
    setGeneratingPdf(id);
    try {
      await generateMosLinkPdf(token, code, name, vorname);
    } finally {
      setGeneratingPdf(null);
    }
  };

  const handleCopy = (id, token) => {
    navigator.clipboard.writeText(buildUrl(token)).catch(() => {});
    setCopied(id);
    setTimeout(() => setCopied(null), 2000);
  };

  return (
    <div className={styles.page}>
      <div className={styles.content}>
      <div className={styles.card}>

        {/* Header */}
        <div className={styles.header}>
          <div>
            <h2 className={styles.title}>MOS-Links</h2>
            <p className={styles.sub}>
              {meta.total > 0
                ? `${meta.total} Link${meta.total !== 1 ? "s" : ""} gesamt`
                : "Einmal-Links für den MOS Fragebogen"}
            </p>
          </div>
          <div className={styles.headerBtns}>
            <button className={styles.refreshBtn} onClick={load} disabled={loading} title="Tabelle aktualisieren">
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                <polyline points="23 4 23 10 17 10"/>
                <path d="M20.49 15a9 9 0 1 1-2.12-9.36L23 10"/>
              </svg>
            </button>
            <button className={styles.createBtn} onClick={openModal} disabled={creating}>
              {creating ? "Wird erstellt…" : "+ Neuen Link erstellen"}
            </button>
          </div>
        </div>

        {/* Search */}
        <div className={styles.searchBar}>
          <svg className={styles.searchIcon} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
          </svg>
          <input
            type="text"
            className={styles.searchInput}
            placeholder="Nach Name oder Vorname suchen…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className={styles.searchClear} onClick={() => setSearch("")}>✕</button>
          )}
        </div>

        {/* Sort selector — replaces the sortable column headers in card view */}
        <div className={styles.mobileSort}>
          <label className={styles.mobileSortLabel} htmlFor="mobileSort">Sortieren:</label>
          <select
            id="mobileSort"
            className={styles.mobileSortSelect}
            value={`${sortBy}:${sortDir}`}
            onChange={(e) => {
              const [col, dir] = e.target.value.split(":");
              setSortBy(col); setSortDir(dir); setPage(1);
            }}
          >
            <option value="created_at:desc">Erstellt am – neueste zuerst</option>
            <option value="created_at:asc">Erstellt am – älteste zuerst</option>
            <option value="status:asc">Status – offene zuerst</option>
            <option value="status:desc">Status – abgeschlossene zuerst</option>
          </select>
        </div>

        {/* Table */}
        {loading ? (
          <p className={styles.hint}>Wird geladen…</p>
        ) : links.length === 0 ? (
          <p className={styles.hint}>Noch keine Links erstellt.</p>
        ) : (
          <div className={styles.tableWrap}>
            <table className={styles.table}>
              <thead>
                <tr>
                  <th>URL</th>
                  <th>Code</th>
                  <th>Vorname</th>
                  <th>Name</th>
                  <SortTh col="status"     label="Status"      sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <SortTh col="created_at" label="Erstellt am" sortBy={sortBy} sortDir={sortDir} onSort={handleSort} />
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {links.map((link) => (
                  <tr key={link.id}>
                    <td data-label="URL">
                      <div className={styles.urlCell}>
                        <span className={styles.urlText}>{buildUrl(link.token)}</span>
                        <button
                          className={styles.copyBtn}
                          onClick={() => handleCopy(link.id, link.token)}
                        >
                          {copied === link.id ? "✓" : "Kopieren"}
                        </button>
                      </div>
                    </td>
                    <td className={styles.codeCell} data-label="Code">
                      <div className={styles.codeWrap}>
                        <span className={styles.codeValue}>
                          {visibleCodes.has(link.id) ? link.code : "••••••••••"}
                        </span>
                        <button className={styles.eyeBtn} onClick={() => toggleCode(link.id)} title={visibleCodes.has(link.id) ? "Verbergen" : "Anzeigen"}>
                          {visibleCodes.has(link.id) ? <EyeOff /> : <EyeOn />}
                        </button>
                      </div>
                    </td>
                    <td className={styles.patientCell} data-label="Vorname">
                      {link.vorname || <span className={styles.patientEmpty}>–</span>}
                    </td>
                    <td className={styles.patientCell} data-label="Name">
                      {link.name || <span className={styles.patientEmpty}>–</span>}
                    </td>
                    <td className={styles.statusTd} data-label="Status">
                      <div className={styles.statusCell}>
                        <span className={Number(link.status) >= 1 ? styles.badgeDone : styles.badgePending}>
                          Vorerhebung {Number(link.status) >= 1 ? "✓" : "–"}
                        </span>
                        <span className={(link.episodes_count ?? 0) > 0 ? styles.badgeDone : styles.badgePending}>
                          {(link.episodes_count ?? 0) > 0
                            ? `${link.episodes_count} EK ✓`
                            : "Noch keine EK"}
                        </span>
                        {link.locked && <span className={styles.badgeLocked}>Gesperrt</span>}
                      </div>
                    </td>
                    <td className={styles.dateCell} data-label="Erstellt am">
                      {new Date(link.created_at).toLocaleDateString("de-DE")}
                    </td>
                    <td className={styles.actionCell}>
                      <div className={styles.actionBtns}>
                        {link.locked && (
                          <button className={styles.unlockBtn} onClick={() => handleUnlock(link.id)}>
                            Entsperren
                          </button>
                        )}
                        <button
                          className={styles.pdfBtn}
                          onClick={() => handlePdf(link.id, link.token, link.code, link.name, link.vorname)}
                          disabled={generatingPdf === link.id}
                          title="PDF herunterladen"
                        >
                          {generatingPdf === link.id ? "…" : "PDF"}
                        </button>
                        <button className={styles.deleteBtn} onClick={() => handleDelete(link.id)}>
                          Löschen
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {/* Pagination — always visible */}
        <div className={styles.pagination}>
          <div className={styles.perPageWrap}>
            <label className={styles.perPageLabel} htmlFor="perPage">Einträge:</label>
            <select
              id="perPage"
              className={styles.perPageSelect}
              value={perPage}
              onChange={handlePerPageChange}
            >
              <option value={10}>10</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
            </select>
          </div>

          <div className={styles.pageControls}>
            <button
              className={styles.pageBtn}
              onClick={() => setPage((p) => p - 1)}
              disabled={page === 1}
            >
              ‹ Zurück
            </button>
            <span className={styles.pageInfo}>
              Seite {meta.current_page} von {meta.last_page}
            </span>
            <button
              className={styles.pageBtn}
              onClick={() => setPage((p) => p + 1)}
              disabled={page >= meta.last_page}
            >
              Weiter ›
            </button>
          </div>
        </div>

      </div>

      <div className={styles.actionsRow}>
        <button type="button" className={styles.primaryBtn} onClick={() => navigate(-1)}>
          Zurück
        </button>
      </div>
      </div>

      {/* ── Create Link Modal ── */}
      {showModal && (
        <div className={styles.modalOverlay} onClick={(e) => { if (e.target === e.currentTarget) closeModal(); }}>
          <div className={styles.modal}>
            <h3 className={styles.modalTitle}>Neuen Link erstellen</h3>
            <div className={styles.modalFields}>
              <label className={styles.modalLabel}>
                Vorname <span className={styles.modalRequired}>*</span>
                <input
                  className={styles.modalInput}
                  type="text"
                  value={modalVorname}
                  onChange={(e) => setModalVorname(e.target.value)}
                  placeholder="Vorname"
                  autoFocus
                  required
                />
              </label>
              <label className={styles.modalLabel}>
                Name <span className={styles.modalRequired}>*</span>
                <input
                  className={styles.modalInput}
                  type="text"
                  value={modalName}
                  onChange={(e) => setModalName(e.target.value)}
                  placeholder="Nachname"
                  required
                />
              </label>
            </div>
            <div className={styles.modalActions}>
              <button className={styles.modalCancelBtn} onClick={closeModal}>
                Abbrechen
              </button>
              <button
                className={styles.modalCreateBtn}
                onClick={handleCreate}
                disabled={creating || !modalName.trim() || !modalVorname.trim()}
              >
                {creating ? "Wird erstellt…" : "Link erstellen"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
